import { FC } from 'react'
import { KeyValuePairs } from '@aws-northstar/ui'
import {
    Box,
    Container,
    Header,
    SpaceBetween
} from '@cloudscape-design/components'
 
import type { ProjectApproval } from '../../../types/projects'
 
interface ApprovalDetailsProps {
    approvalDetails: ProjectApproval
    projectDetails?: Record<string, any>
}
 
const unwrapDetailScalar = (value: unknown): unknown => {
    if (value != null && typeof value === 'object' && 'value' in value) {
        return (value as { value: unknown }).value
    }
    return value
}

const normalizeWorkloadType = (value: unknown): string => {
    const raw = String(unwrapDetailScalar(value) ?? '')
        .trim()
        .toLowerCase()
        .replace(/[_\s]+/g, '-')
    if (raw === 'eks') return 'EKS'
    if (raw === 'non-eks') return 'Non-EKS'
    if (raw.includes('non') && raw.includes('eks')) return 'Non-EKS'
    if (raw.includes('eks')) return 'EKS'
    return 'Non-EKS'
}

const formatDiscoveryTagsApproval = (v: unknown): string => {
	if (v == null) return '-'
	if (typeof v === 'object' && v !== null) {
		const row = v as { label?: unknown; purpose?: unknown; description?: unknown }
		if (row.purpose != null && String(row.purpose).trim()) {
			return `${String(row.label ?? row.purpose)} (${String(row.purpose)})`
		}
		if ('label' in v) {
			return String((v as { label?: unknown }).label ?? '-')
		}
	}
	return String(v)
}

const formatEksNamespaces = (v: unknown): string => {
    if (!Array.isArray(v) || v.length === 0) return '-'
    return v.map((x) => String(x)).filter(Boolean).join(', ')
}

const formatEksSubApps = (v: unknown): string => {
    if (!Array.isArray(v) || v.length === 0) return '-'
    return v
        .map((r: Record<string, unknown>) => {
            const name = String(r.subAppName ?? r.name ?? '').trim()
            const ns = String(r.targetNamespace ?? '').trim()
            if (!name && !ns) return ''
            return `${name || '(unnamed)'} → ${ns || '(no namespace)'}`
        })
        .filter(Boolean)
        .join('; ')
}

const resolveVpcAliasesFromApproval = (
	details: Record<string, unknown> | undefined
): { identifier: string; value: string } => {
	if (!details) return { identifier: 'tag:Name', value: '-' }
	const nested = details.VpcAliases ?? details.vpcAliases
	const fromNested =
		nested && typeof nested === 'object'
			? {
					identifier: String(
						(nested as { identifier?: unknown }).identifier ?? ''
					).trim(),
					value: String((nested as { value?: unknown }).value ?? '').trim()
			  }
			: { identifier: '', value: '' }
	return {
		identifier:
			String(details.vpcIdentifier ?? fromNested.identifier ?? 'tag:Name').trim() ||
			'tag:Name',
		value: String(details.vpcValue ?? fromNested.value ?? '').trim() || '-'
	}
}

const ApprovalDetails: FC<ApprovalDetailsProps> = ({ approvalDetails, projectDetails }) => {
    const approvalDetailsKeyValuePairsItems = [
        [{ label: 'Approver', value: approvalDetails?.approverID }],
        [{ label: 'Approved Date', value: approvalDetails?.timestamp }]
    ]
 
    const platformNametags = [
        [{ label: 'Platform Name', value: approvalDetails?.details?.platformName }]
    ]
 
    const vpcAliases = resolveVpcAliasesFromApproval(
        approvalDetails?.details as Record<string, unknown> | undefined
    )

    const generalDetailsKeyValuePairsItems = [
        [{ label: 'DNS Zone Apex', value: approvalDetails?.details?.dnsZoneApex }],
        [{ label: 'VPC Identifier', value: vpcAliases.identifier }],
        [{ label: 'VPC Value', value: vpcAliases.value }],
        ...(normalizeWorkloadType(
            approvalDetails?.details?.accountWorkloadType ??
                projectDetails?.accountWorkloadType
        ) === 'EKS'
            ? [
                  [
                      {
                          label: 'Workload Cluster Name',
                          value:
                              approvalDetails?.details?.workloadClusterName ?? '-'
                      }
                  ],
                  [
                      {
                          label: 'Workload cluster (repo / key)',
                          value:
                              String(
                                  approvalDetails?.details
                                      ?.workloadClusterWorkloadRepoNameEnv ?? ''
                              ).trim() || '-'
                      }
                  ],
                  [
                      {
                          label: 'Workload Cluster Version',
                          value:
                              approvalDetails?.details?.workloadClusterVersion ?? '-'
                      }
                  ],
                  [
                      {
                          label: 'Discovery Tags',
                          value: formatDiscoveryTagsApproval(
                              approvalDetails?.details?.discoveryTags
                          )
                      }
                  ],
                  [
                      {
                          label: 'Custom NameSpaces',
                          value: formatEksNamespaces(
                              approvalDetails?.details?.eksCustomNamespaces
                          )
                      }
                  ],
                  [
                      {
                          label: 'Sub Apps',
                          value: formatEksSubApps(approvalDetails?.details?.eksSubApps)
                      }
                  ]
              ]
            : [])
    ]

    const kmsDetailsKeyValuePairsItems = [
        [{ label: 'Role Name', value: approvalDetails?.details?.roleName || approvalDetails?.details?.kms[0]?.roleName }],
        [
            {
                label: 'KMS AWS Account ID',
                value: approvalDetails?.details?.awsAccountId?.value || approvalDetails?.details?.kms[0]?.awsAccountId
            }
        ],
        [
            {
				label: 'Delegate AWS Account ID(s)',
				value: approvalDetails?.details?.delegateAWSAccountIDs?.join(', ') || approvalDetails?.details?.kms[0].delegateAWSAccountIDs
				.map(
					(delegateAwsAccountId: { dismissLabel: string; label: string }) =>
						delegateAwsAccountId.label
				)
				.join(', ')

			}
        ]
    ]
 
    const renderTFProvidersDetails = () => {
        return approvalDetails?.details?.tfProviders?.map(
            (
                tfProvider: {
                    alias: string
                    externalId: string
                    region: string
                    arn: string
                },
                i: number
            ) => {
                return (
                    <Box variant="awsui-key-label" key={i}>
                        <p key={i}>{tfProvider.alias}</p>
                        <KeyValuePairs
                            key={`tfProvidersDetails${i}`}
                            items={[
                                [{ label: 'External ID', value: tfProvider.externalId }],
                                [{ label: 'Region', value: tfProvider.region }],
                                [{ label: 'ARN', value: tfProvider.arn }]
                            ]}
                        />
                    </Box>
                )
            }
        )
    }
 
    const renderSubnetAliasesDetails = () => {
        return approvalDetails?.details?.subnetAliases?.map(
            (
                subnetAlias: {
                    count: string
                    alias: string
                    identifier: string
                    value: string
                },
                i: number
            ) => {
                return (
                    <Box variant="awsui-key-label" key={i}>
                        <p key={i}>{subnetAlias.alias}</p>
                        <KeyValuePairs
                            key={`subnetAliasesDetails${i}`}
                            items={[
                                [{ label: 'Count', value: subnetAlias.count }],
                                [{ label: 'Identifier', value: subnetAlias.identifier }],
                                [{ label: 'Value', value: subnetAlias.value }]
                            ]}
                        />
                    </Box>
                )
            }
        )
    }
 
    const securityAliasesKeyValuePairsItems = [
        approvalDetails?.details?.securityAliases.map(
            (securityAlias: { value: string; existing: boolean; key: string }) => {
                return {
                    label: securityAlias.key,
                    value: securityAlias.value
                }
            }
        )
    ]
 
    const tagsKeyValuePairsItems = [
        approvalDetails?.details?.tags.map(
            (tag: { value: string; existing: boolean; key: string }) => {
                return {
                    label: tag.key,
                    value: tag.value
                }
            }
        )
    ]
 
    return (
        <Container header={<Header variant="h3">Approval Details</Header>}>
            <SpaceBetween direction="vertical" size="l">
                <KeyValuePairs
                    key={'approvalDetails'}
                    items={approvalDetailsKeyValuePairsItems}
                />
                <Box variant="h5">
                    <KeyValuePairs key={'Platform Name'} items={platformNametags} />
                </Box>
 
                <Box variant="h5">
                    <p>General Details</p>
                    <KeyValuePairs
                        key={'generalDetails'}
                        items={generalDetailsKeyValuePairsItems}
                    />
                </Box>

                <Box variant="h5">
                    <p>KMS</p>
                    <KeyValuePairs
                        key={'kmsDetails'}
                        items={kmsDetailsKeyValuePairsItems}
                    />
                </Box>
 
                <Box variant="h5">
                    <p>TF Providers</p>
                    {renderTFProvidersDetails()}
                </Box>
 
                <Box variant="h5">
                    <p>Subnet Aliases</p>
                    {renderSubnetAliasesDetails()}
                </Box>
 
                <Box variant="h5">
                    <p>Security Aliases</p>
                    <KeyValuePairs
                        key={'securityAliasesDetails'}
                        items={securityAliasesKeyValuePairsItems}
                    />
                </Box>
 
                <Box variant="h5">
                    <p>Tags</p>
                    <KeyValuePairs key={'tagsDetails'} items={tagsKeyValuePairsItems} />
                </Box>
            </SpaceBetween>
        </Container>
    )
}
 
export default ApprovalDetails
